import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { CheckCircle2, ChevronDown, ChevronRight, Eye, Phone, PhoneMissed, RefreshCcw, RotateCw, ShoppingBag, UserCheck } from 'lucide-react';
import {
  claimMissedCall,
  fetchMissedCallOrders,
  fetchMissedCalls,
  fetchTaskTranscript,
  getTaskAudioUrl,
  markMissedCallProcessed,
  refreshMissedCallOrder,
  unclaimMissedCall,
  unmarkMissedCallProcessed
} from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import DateRangePicker from './DateRangePicker';
import { useToast } from '../hooks/use-toast';

const MOSCOW_TIME_ZONE = 'Europe/Moscow';
const ORDER_LOOKUP_BATCH_SIZE = 2;
const MIN_WAIT_SECONDS = 30;
const LOADING_TRANSCRIPT = 'loading';
const CALL_TYPE_LABELS = {
  order_new: 'Новый заказ',
  order_confirm_outbound: 'Подтверждение заказа',
  order_change: 'Изменение заказа',
  delivery_issue: 'Жалоба',
  consultation: 'Консультация',
  callback_missed: 'Пропущенный дозвон',
  wrong_number: 'Ошибочный номер'
};

const MissedCalls = () => {
  const { toast } = useToast();
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [claimingPhone, setClaimingPhone] = useState(null);
  const [processingPhone, setProcessingPhone] = useState(null);
  const [refreshingOrderKeys, setRefreshingOrderKeys] = useState(() => new Set());
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [expandedCalls, setExpandedCalls] = useState(() => new Set());
  const [transcriptsMap, setTranscriptsMap] = useState(() => new Map());
  const [now, setNow] = useState(Date.now());
  const [dateRange, setDateRange] = useState(() => {
    const todayInMoscow = getTodayInMoscow();
    return {
      from: todayInMoscow,
      to: todayInMoscow
    };
  });
  const [filterShortCalls, setFilterShortCalls] = useState(true);
  const groupsRef = useRef([]);
  const orderCacheRef = useRef(new Map());
  const orderLookupInFlightRef = useRef(new Set());
  const orderLookupGenerationRef = useRef(0);
  const orderLookupWorkerActiveRef = useRef(false);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    orderLookupGenerationRef.current += 1;
    orderLookupInFlightRef.current.clear();
  }, [dateRange]);

  useEffect(() => {
    if (!selectedGroup) {
      return;
    }

    const updatedGroup = groups.find((group) => buildGroupClientKey(group) === buildGroupClientKey(selectedGroup));
    if (!updatedGroup) {
      setSelectedGroup(null);
      return;
    }

    if (updatedGroup !== selectedGroup) {
      setSelectedGroup(updatedGroup);
    }
  }, [groups, selectedGroup]);

  const loadMissedCalls = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchMissedCalls({
        date_from: format(dateRange.from, 'yyyy-MM-dd'),
        date_to: format(dateRange.to, 'yyyy-MM-dd')
      });
      setGroups(mergeGroupsWithCachedOrders(filterVisibleMissedCallGroups(response.groups ?? []), orderCacheRef.current));
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить список пропущенных звонков.');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    if (orderLookupWorkerActiveRef.current) {
      return undefined;
    }

    const hasPendingOrders = groups.some((group) => !group.orderLookupCompleted);
    if (!hasPendingOrders) {
      return undefined;
    }

    orderLookupWorkerActiveRef.current = true;
    const generation = orderLookupGenerationRef.current;
    let disposed = false;

    const run = async () => {
      try {
        while (!disposed && generation === orderLookupGenerationRef.current) {
          const pendingBatch = groupsRef.current
            .filter((group) => !group.orderLookupCompleted && !orderLookupInFlightRef.current.has(buildGroupClientKey(group)))
            .slice(0, ORDER_LOOKUP_BATCH_SIZE);

          if (pendingBatch.length === 0) {
            break;
          }

          pendingBatch.forEach((group) => {
            orderLookupInFlightRef.current.add(buildGroupClientKey(group));
          });

          try {
            const response = await fetchMissedCallOrders(
              pendingBatch.map((group) => ({
                clientPhone: group.clientPhone,
                groupDate: group.groupDate,
                lastCallTime: group.lastCallTime
              }))
            );

            if (disposed || generation !== orderLookupGenerationRef.current) {
              return;
            }

            const items = response.items ?? [];
            items.forEach((item) => {
              orderCacheRef.current.set(buildGroupClientKey(item), {
                lastOrder: item.lastOrder ?? null,
                lastCallTime: findGroupLastCallTime(pendingBatch, item),
                orderLookupCompleted: true
              });
            });

            setGroups((previous) =>
              previous.map((group) => {
                const matched = items.find((item) => buildGroupClientKey(item) === buildGroupClientKey(group));
                if (!matched) {
                  return group;
                }

                return {
                  ...group,
                  lastOrder: matched.lastOrder ?? null,
                  orderLookupCompleted: true
                };
              })
            );
          } catch (err) {
            console.error(err);
            return;
          } finally {
            pendingBatch.forEach((group) => {
              orderLookupInFlightRef.current.delete(buildGroupClientKey(group));
            });
          }
        }
      } finally {
        orderLookupWorkerActiveRef.current = false;
      }
    };

    void run();

    return () => {
      disposed = true;
    };
  }, [groups]);

  useEffect(() => {
    loadMissedCalls();
  }, [loadMissedCalls]);

  useEffect(() => {
    const refreshId = window.setInterval(() => {
      loadMissedCalls();
    }, 60_000);

    return () => window.clearInterval(refreshId);
  }, [loadMissedCalls]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setNow(Date.now());
    }, 30_000);

    return () => window.clearInterval(timerId);
  }, []);

  const visibleGroups = useMemo(
    () =>
      filterShortCalls
        ? groups.filter((group) => (group.maxWaitDurationSeconds ?? 0) >= MIN_WAIT_SECONDS)
        : groups,
    [groups, filterShortCalls]
  );

  const callAnalyticsMap = useMemo(() => buildCallAnalyticsMap(groups), [groups]);

  const loadTranscript = useCallback(async (taskId) => {
    if (!taskId) {
      return;
    }

    const existing = transcriptsMap.get(taskId);
    if (existing !== undefined) {
      return;
    }

    setTranscriptsMap((previous) => {
      const next = new Map(previous);
      if (!next.has(taskId)) {
        next.set(taskId, LOADING_TRANSCRIPT);
      }
      return next;
    });

    try {
      const transcript = await fetchTaskTranscript(taskId);
      setTranscriptsMap((previous) => {
        const next = new Map(previous);
        next.set(taskId, transcript || null);
        return next;
      });
    } catch (err) {
      console.error(err);
      setTranscriptsMap((previous) => {
        const next = new Map(previous);
        next.set(taskId, null);
        return next;
      });
    }
  }, [transcriptsMap]);

  const toggleCallExpanded = useCallback((entryId, taskId) => {
    setExpandedCalls((previous) => {
      const next = new Set(previous);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });

    if (taskId) {
      void loadTranscript(taskId);
    }
  }, [loadTranscript]);

  const handleRefreshOrder = useCallback(async (group) => {
    const groupKey = buildGroupClientKey(group);
    setRefreshingOrderKeys((prev) => new Set([...prev, groupKey]));
    try {
      const result = await refreshMissedCallOrder({
        clientPhone: group.clientPhone,
        groupDate: group.groupDate,
        lastCallTime: group.lastCallTime
      });
      const updatedOrder = result?.lastOrder ?? null;
      orderCacheRef.current.set(groupKey, { lastOrder: updatedOrder, orderLookupCompleted: true });
      setGroups((prev) =>
        prev.map((g) =>
          buildGroupClientKey(g) === groupKey
            ? { ...g, lastOrder: updatedOrder, orderLookupCompleted: true }
            : g
        )
      );
    } catch (err) {
      console.error(err);
      toast({ title: 'Ошибка', description: 'Не удалось обновить данные заказа.', variant: 'destructive' });
    } finally {
      setRefreshingOrderKeys((prev) => {
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
    }
  }, [toast]);

  const handleClaim = useCallback(async (groupToClaim) => {
    const groupKey = buildGroupClientKey(groupToClaim);
    setClaimingPhone(groupKey);

    try {
      const result = await claimMissedCall(groupToClaim.clientPhone, {
        claim_date: groupToClaim.groupDate
      });
      setGroups((previous) =>
        previous.map((group) =>
          buildGroupClientKey(group) === groupKey
            ? {
                ...group,
                isClaimed: true,
                isClaimedByCurrentUser: result.isClaimedByCurrentUser,
                claimedByUsername: result.claimedByUsername,
                claimedAt: result.claimedAt
              }
            : group
        )
      );

      toast({
        title: result.isClaimedByCurrentUser ? 'Звонок взят в работу' : 'Звонок уже взят',
        description: result.isClaimedByCurrentUser
          ? 'Группа закреплена за текущим пользователем.'
          : `Группа уже у ${result.claimedByUsername}.`
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Не удалось взять звонок в работу',
        description: err?.response?.data?.message ?? 'Попробуйте ещё раз.',
        variant: 'destructive'
      });
    } finally {
      setClaimingPhone(null);
    }
  }, [toast]);

  const handleUnclaim = useCallback(async (groupToUnclaim) => {
    const groupKey = buildGroupClientKey(groupToUnclaim);
    setClaimingPhone(groupKey);

    try {
      await unclaimMissedCall(groupToUnclaim.clientPhone, {
        claim_date: groupToUnclaim.groupDate
      });

      setGroups((previous) =>
        previous.map((group) =>
          buildGroupClientKey(group) === groupKey
            ? {
                ...group,
                isClaimed: false,
                isClaimedByCurrentUser: false,
                claimedByUsername: null,
                claimedAt: null
              }
            : group
        )
      );

      toast({
        title: 'Звонок возвращён',
        description: 'Группа снята с вас.'
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Не удалось вернуть звонок',
        description: err?.response?.data?.message ?? 'Попробуйте ещё раз.',
        variant: 'destructive'
      });
    } finally {
      setClaimingPhone(null);
    }
  }, [toast]);

  const handleMarkProcessed = useCallback(async (groupToMark) => {
    const groupKey = buildGroupClientKey(groupToMark);
    setProcessingPhone(groupKey);

    try {
      const result = await markMissedCallProcessed(groupToMark.clientPhone, {
        claim_date: groupToMark.groupDate
      });
      setGroups((previous) =>
        previous.map((group) =>
          buildGroupClientKey(group) === groupKey
            ? {
                ...group,
                isProcessed: true,
                isProcessedByCurrentUser: result.isProcessedByCurrentUser,
                processedByUsername: result.processedByUsername,
                processedAt: result.processedAt
              }
            : group
        )
      );

      toast({
        title: 'Группа помечена обработанной',
        description: 'Метка снимется автоматически, когда поступит новый пропущенный звонок.'
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Не удалось пометить обработанной',
        description: err?.response?.data?.message ?? 'Попробуйте ещё раз.',
        variant: 'destructive'
      });
    } finally {
      setProcessingPhone(null);
    }
  }, [toast]);

  const handleUnmarkProcessed = useCallback(async (groupToUnmark) => {
    const groupKey = buildGroupClientKey(groupToUnmark);
    setProcessingPhone(groupKey);

    try {
      await unmarkMissedCallProcessed(groupToUnmark.clientPhone, {
        claim_date: groupToUnmark.groupDate
      });
      setGroups((previous) =>
        previous.map((group) =>
          buildGroupClientKey(group) === groupKey
            ? {
                ...group,
                isProcessed: false,
                isProcessedByCurrentUser: false,
                processedByUsername: null,
                processedAt: null
              }
            : group
        )
      );

      toast({
        title: 'Метка обработки снята'
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Не удалось снять метку',
        description: err?.response?.data?.message ?? 'Попробуйте ещё раз.',
        variant: 'destructive'
      });
    } finally {
      setProcessingPhone(null);
    }
  }, [toast]);

  const stats = useMemo(() => {
    const waiting = visibleGroups.filter((group) => !group.isClaimed && !group.isResolved).length;
    const inProgress = visibleGroups.filter((group) => group.isClaimed && !group.isResolved).length;
    const resolved = visibleGroups.filter((group) => group.isResolved).length;

    return {
      visible: visibleGroups.length,
      waiting,
      inProgress,
      resolved
    };
  }, [visibleGroups]);

  const sortedGroups = useMemo(
    () =>
      [...visibleGroups].sort((left, right) => {
        const priority = (group) => {
          if (group.isResolved) {
            return 3;
          }

          if (group.isProcessed) {
            return 2;
          }

          if (group.isClaimed) {
            return 1;
          }

          return 0;
        };

        const diff = priority(left) - priority(right);
        if (diff !== 0) {
          return diff;
        }

        const rightTime = normalizeMoscowDate(right.lastCallTime)?.getTime() ?? 0;
        const leftTime = normalizeMoscowDate(left.lastCallTime)?.getTime() ?? 0;
        return rightTime - leftTime;
      }),
    [visibleGroups]
  );

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
              <PhoneMissed className="h-3.5 w-3.5" />
              Missed Calls
            </div>
            <CardTitle className="text-2xl text-slate-900">Пропущенные звонки</CardTitle>
            <CardDescription className="max-w-2xl text-sm text-slate-600">
              По умолчанию показывается текущий день по Москве. При необходимости можно выбрать любой интервал и посмотреть пропущенные звонки за выбранные даты.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <DateRangePicker value={dateRange} onChange={setDateRange} className="w-full sm:w-[320px]" />
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
              <Switch checked={filterShortCalls} onCheckedChange={setFilterShortCalls} />
              Только реальные (≥{MIN_WAIT_SECONDS}с)
            </label>
            <Button variant="outline" className="gap-2" onClick={loadMissedCalls} disabled={isLoading}>
              <RefreshCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Обновить
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <StatCard label="На экране" value={stats.visible} tone="slate" />
          <StatCard label="Ждут оператора" value={stats.waiting} tone="amber" />
          <StatCard label="В работе / дозвонились" value={`${stats.inProgress} / ${stats.resolved}`} tone="emerald" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Список клиентов</CardTitle>
            <CardDescription>
              Период: {formatDateRange(dateRange)} · Обновляется автоматически раз в минуту · Звонков {visibleGroups.reduce((sum, g) => sum + (g.calls?.length ?? 0), 0).toLocaleString('ru-RU')} в {visibleGroups.length.toLocaleString('ru-RU')} группах{filterShortCalls && groups.length !== visibleGroups.length ? ` (скрыто групп: ${(groups.length - visibleGroups.length).toLocaleString('ru-RU')})` : ''}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Телефон</TableHead>
                  <TableHead>Последний звонок</TableHead>
                  <TableHead>Последний заказ</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead className="w-10" />
                  <TableHead className="text-right">Действие</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                      Загрузка пропущенных клиентов...
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && sortedGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                      Подходящих пропущенных звонков не найдено.
                    </TableCell>
                  </TableRow>
                )}

                {sortedGroups.map((group) => (
                  <TableRow key={buildGroupClientKey(group)} className={group.isResolved || group.isProcessed ? 'opacity-50' : ''}>
                    <TableCell className="min-w-[220px]">
                      <div className="font-medium text-slate-900">{group.clientPhone || '—'}</div>
                      <div className="text-xs text-slate-500">{formatCallCount(group.callCount)}</div>
                      <GroupBadges calls={group.calls} analyticsMap={callAnalyticsMap} className="mt-2" />
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <div className="font-medium text-slate-900">{formatCallTime(group.lastCallTime)}</div>
                      <div className="text-xs text-slate-500">{formatRelativeWait(group.lastCallTime, now)}</div>
                    </TableCell>
                    <TableCell className="min-w-[320px]">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          {group.lastOrder ? (
                            <div className="space-y-1 text-sm text-slate-700">
                              <div className="font-medium text-slate-900">
                                {translateOrderStatus(group.lastOrder.status)} · {translateOrderType(group.lastOrder.orderType)}
                                {group.lastOrder.orderNumber ? ` · #${group.lastOrder.orderNumber}` : ''}
                              </div>
                              {group.lastOrder.customerName && (
                                <div className="text-slate-800">{group.lastOrder.customerName}</div>
                              )}
                              {group.lastOrder.conception && (
                                <div className="font-medium text-slate-700">{group.lastOrder.conception}</div>
                              )}
                              <div className="text-slate-600">{group.lastOrder.address || '—'}</div>
                              <div className="text-xs text-slate-500">
                                Заказ: {formatOrderTime(group.lastOrder.orderDate)}
                                {' · '}
                                Доставлен: {formatOrderTime(group.lastOrder.delivered)}
                              </div>
                              <div className="text-xs text-slate-500">
                                Оператор: {group.lastOrder.operator || '—'}
                                {' · '}
                                Курьер: {group.lastOrder.courier || '—'}
                              </div>
                              {group.lastOrder.comment && (
                                <div className="text-xs text-slate-500 italic">{group.lastOrder.comment}</div>
                              )}
                            </div>
                          ) : group.orderLookupCompleted ? (
                            <span className="text-sm text-muted-foreground">Заказ не найден</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">Загружаем данные заказа...</span>
                          )}
                        </div>
                        {group.orderLookupCompleted && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 shrink-0"
                            title="Обновить данные заказа"
                            disabled={refreshingOrderKeys.has(buildGroupClientKey(group))}
                            onClick={() => handleRefreshOrder(group)}
                          >
                            <RotateCw className={`h-3.5 w-3.5 ${refreshingOrderKeys.has(buildGroupClientKey(group)) ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {renderStatusBadge(group)}
                    </TableCell>
                    <TableCell className="w-10">
                      <Button size="sm" variant="ghost" onClick={() => setSelectedGroup(group)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      {renderActionButtons(group, {
                        claimingPhone,
                        processingPhone,
                        handleClaim,
                        handleUnclaim,
                        handleMarkProcessed,
                        handleUnmarkProcessed
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <GroupDetailsModal
        group={selectedGroup}
        onClose={() => setSelectedGroup(null)}
        expandedCalls={expandedCalls}
        analyticsMap={callAnalyticsMap}
        transcriptsMap={transcriptsMap}
        onToggleCallExpanded={toggleCallExpanded}
        onLoadTranscript={loadTranscript}
        onRefreshOrder={handleRefreshOrder}
        isRefreshingOrder={selectedGroup ? refreshingOrderKeys.has(buildGroupClientKey(selectedGroup)) : false}
      />
    </div>
  );
};

const GroupDetailsModal = ({
  group,
  onClose,
  expandedCalls,
  analyticsMap,
  transcriptsMap,
  onToggleCallExpanded,
  onLoadTranscript,
  onRefreshOrder,
  isRefreshingOrder
}) => {
  const calls = Array.isArray(group?.calls) ? group.calls : [];
  const lastOrder = group?.lastOrder ?? null;

  return (
    <Dialog
      open={Boolean(group)}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Звонки клиента {group?.clientPhone || '—'}</DialogTitle>
          <DialogDescription>
            Группа за {group?.groupDate || '—'} · {formatCallCount(group?.callCount ?? calls.length)} звонков
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Phone className="h-4 w-4" />
              Звонки
            </div>
            {calls.length === 0 ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Нет данных о звонках
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead>Статус</TableHead>
                      <TableHead>Тип звонка</TableHead>
                      <TableHead>Направление</TableHead>
                      <TableHead>Оператор</TableHead>
                      <TableHead>Дата и время</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calls.map((call, index) => {
                      const isExpanded = expandedCalls.has(call.entryId);
                      const canExpand = Boolean(call.taskId);
                      const Icon = isExpanded ? ChevronDown : ChevronRight;
                      const analytics = call.taskId ? analyticsMap.get(call.taskId) : null;

                      return (
                        <React.Fragment key={call.entryId ?? `${call.callTime ?? 'call'}-${index}`}>
                          <TableRow
                            className={canExpand ? 'cursor-pointer' : ''}
                            onClick={() => canExpand && onToggleCallExpanded(call.entryId, call.taskId)}
                          >
                            <TableCell>
                              {canExpand ? <Icon className="h-4 w-4 text-slate-500" /> : null}
                            </TableCell>
                            <TableCell>
                              <CallTypeBadge call={call} />
                            </TableCell>
                            <TableCell>
                              <CallAnalyticsBadge analytics={analytics} />
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-700">{call.callDirection || '—'}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-700">{call.operatorName || '—'}</span>
                            </TableCell>
                            <TableCell>
                              <div>{formatCallTime(call.callTime)}</div>
                              {call.isMissed && (
                                <div className="text-xs text-slate-500">
                                  Ожидание: {call.waitFormatted || formatDuration(call.waitDurationSeconds)}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={6} className="bg-slate-50/70 p-0">
                                <CallDetailPanel
                                  taskId={call.taskId}
                                  transcriptsMap={transcriptsMap}
                                  onLoadTranscript={onLoadTranscript}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ShoppingBag className="h-4 w-4" />
                Заказы
              </div>
              {group?.orderLookupCompleted && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 text-xs"
                  disabled={isRefreshingOrder}
                  onClick={() => onRefreshOrder(group)}
                >
                  <RotateCw className={`h-3 w-3 ${isRefreshingOrder ? 'animate-spin' : ''}`} />
                  {isRefreshingOrder ? 'Обновляем...' : 'Обновить'}
                </Button>
              )}
            </div>
            {!group?.orderLookupCompleted ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Загружаем данные заказа...
              </div>
            ) : !lastOrder ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Заказов не найдено
              </div>
            ) : (
              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="font-medium text-slate-900">
                  {translateOrderStatus(lastOrder.status)} · {translateOrderType(lastOrder.orderType)}
                  {lastOrder.orderNumber ? ` · #${lastOrder.orderNumber}` : ''}
                </div>
                {lastOrder.customerName && (
                  <div className="font-medium text-slate-800">Клиент: {lastOrder.customerName}</div>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div className="text-slate-500">Точка</div>
                  <div>{lastOrder.conception || '—'}</div>
                  <div className="text-slate-500">Адрес</div>
                  <div>{lastOrder.address || '—'}</div>
                  {lastOrder.deliveryPointComment && (
                    <>
                      <div className="text-slate-500">Комм. к адресу</div>
                      <div>{lastOrder.deliveryPointComment}</div>
                    </>
                  )}
                  {lastOrder.comment && (
                    <>
                      <div className="text-slate-500">Комментарий</div>
                      <div className="italic">{lastOrder.comment}</div>
                    </>
                  )}
                  <div className="text-slate-500">Дата заказа</div>
                  <div>{formatOrderTime(lastOrder.orderDate)}</div>
                  <div className="text-slate-500">Доставлен</div>
                  <div>{formatOrderTime(lastOrder.delivered)}</div>
                  {(lastOrder.completeBefore || lastOrder.isAsap) && (
                    <>
                      <div className="text-slate-500">Срок доставки</div>
                      <div>
                        {lastOrder.isAsap ? 'Как можно скорее' : formatOrderTime(lastOrder.completeBefore)}
                      </div>
                    </>
                  )}
                  <div className="text-slate-500">Оператор</div>
                  <div>{lastOrder.operator || '—'}</div>
                  <div className="text-slate-500">Курьер</div>
                  <div>{lastOrder.courier || '—'}</div>
                  {lastOrder.cancelCauseName && (
                    <>
                      <div className="text-red-600">Причина отмены</div>
                      <div className="text-red-700">{lastOrder.cancelCauseName}</div>
                    </>
                  )}
                </div>
                {Array.isArray(lastOrder.items) && lastOrder.items.length > 0 && (
                  <div className="mt-2">
                    <div className="mb-1 font-medium text-slate-800">Состав заказа</div>
                    <div className="space-y-1">
                      {lastOrder.items.map((item, idx) => (
                        <div key={idx} className="flex items-baseline justify-between gap-2 text-xs">
                          <span className="flex-1">
                            {item.name}
                            {item.amount !== 1 && ` × ${item.amount}`}
                            {item.comment && <span className="ml-1 italic text-slate-400">({item.comment})</span>}
                          </span>
                          <span className="shrink-0 text-slate-500">{item.price} руб.</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const resolveMissedCallLabel = (call) => {
  if (!call?.isMissed) {
    return 'Дозвонился';
  }

  const normalizedDirection = String(call.callDirection ?? '').trim().toLowerCase();
  return normalizedDirection === 'исходящий' ? 'Недозвон' : 'Пропущен';
};

const isIncomingMissedCall = (call) => {
  if (!call?.isMissed) {
    return false;
  }

  const normalizedDirection = String(call.callDirection ?? '').trim().toLowerCase();
  return normalizedDirection === 'входящий';
};

const filterVisibleMissedCallGroups = (groups) =>
  (Array.isArray(groups) ? groups : []).filter((group) => (group.calls ?? []).some(isIncomingMissedCall));

const CallTypeBadge = ({ call }) => {
  const label = resolveMissedCallLabel(call);

  if (call?.isMissed) {
    return (
      <div className="inline-flex items-center gap-2">
        <PhoneMissed className="h-4 w-4 text-red-600" />
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          {label}
        </Badge>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2">
      <Phone className="h-4 w-4 text-emerald-600" />
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        {label}
      </Badge>
    </div>
  );
};

const CallAnalyticsBadge = ({ analytics }) => {
  if (!analytics?.callType) {
    return <span className="text-sm text-slate-500">—</span>;
  }

  return (
    <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-800">
      {analytics.callType}
    </Badge>
  );
};

const CallDetailPanel = ({ taskId, transcriptsMap, onLoadTranscript }) => {
  useEffect(() => {
    if (taskId) {
      void onLoadTranscript(taskId);
    }
  }, [taskId, onLoadTranscript]);

  if (!taskId) {
    return (
      <div className="px-4 py-4 text-sm text-slate-600">
        Для этого звонка нет связанной аналитической задачи.
      </div>
    );
  }

  const transcript = transcriptsMap.get(taskId);
  const isLoading = transcript === LOADING_TRANSCRIPT;

  return (
    <div className="space-y-4 px-4 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <audio className="w-full" src={getTaskAudioUrl(taskId)} controls />
        <span className="text-xs text-slate-500">Task: {taskId}</span>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-2 text-sm font-semibold text-slate-900">Транскрипция</div>
        {isLoading ? (
          <div className="text-sm text-slate-500">Загружаем транскрипцию...</div>
        ) : transcript ? (
          <pre className="whitespace-pre-wrap break-words text-sm text-slate-700">{transcript}</pre>
        ) : (
          <div className="text-sm text-slate-500">Транскрипция недоступна.</div>
        )}
      </div>
    </div>
  );
};

const GroupBadges = ({ calls, analyticsMap, className = '' }) => {
  const badges = [];

  for (const call of calls ?? []) {
    const analytics = call?.taskId ? analyticsMap.get(call.taskId) : null;
    if (!analytics || analytics.loading) {
      continue;
    }

    if (analytics.callType) {
      badges.push({ label: analytics.callType, className: 'border-slate-300 bg-slate-50 text-slate-700' });
    }

    if (analytics.isNegative) {
      badges.push({ label: 'Негатив', className: 'border-red-200 bg-red-50 text-red-700' });
    }

    if (analytics.hasComplaint) {
      badges.push({ label: 'Жалоба', className: 'border-orange-200 bg-orange-50 text-orange-700' });
    }
  }

  if (badges.length === 0) {
    return null;
  }

  const uniqueBadges = [...new Map(badges.map((badge) => [badge.label, badge])).values()];

  return (
    <div className={`flex flex-wrap gap-1 ${className}`.trim()}>
      {uniqueBadges.map((badge) => (
        <Badge key={badge.label} variant="outline" className={badge.className}>
          {badge.label}
        </Badge>
      ))}
    </div>
  );
};

const renderStatusBadge = (group) => {
  if (group.isResolved) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        Дозвонился
      </Badge>
    );
  }

  if (group.isProcessed) {
    return (
      <Badge
        className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100"
        title={group.processedByUsername ? `Обработано: ${group.processedByUsername}` : undefined}
      >
        {group.processedByUsername ? `Обработано: ${group.processedByUsername}` : 'Обработано'}
      </Badge>
    );
  }

  if (group.isClaimed) {
    return (
      <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">
        {group.claimedByUsername ? `В работе: ${group.claimedByUsername}` : 'В работе'}
      </Badge>
    );
  }

  return (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
      Ожидает
    </Badge>
  );
};

const renderClaimButton = (group, claimingPhone, handleClaim, handleUnclaim) => {
  const key = buildGroupClientKey(group);

  if (group.isClaimedByCurrentUser) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="gap-2"
        disabled={claimingPhone === key}
        onClick={() => handleUnclaim(group)}
      >
        <UserCheck className="h-4 w-4" />
        {claimingPhone === key ? 'Возврат...' : 'В работе у меня'}
      </Button>
    );
  }

  if (group.isClaimed) {
    return (
      <Button size="sm" variant="outline" disabled>
        {`Взят: ${group.claimedByUsername || 'оператор'}`}
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      className="gap-2"
      disabled={claimingPhone === key}
      onClick={() => handleClaim(group)}
    >
      <UserCheck className="h-4 w-4" />
      {claimingPhone === key ? 'Назначение...' : 'Взять в работу'}
    </Button>
  );
};

const renderProcessedButton = (group, processingPhone, handleMarkProcessed, handleUnmarkProcessed) => {
  const key = buildGroupClientKey(group);

  if (group.isProcessed && group.isProcessedByCurrentUser) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="gap-2"
        disabled={processingPhone === key}
        onClick={() => handleUnmarkProcessed(group)}
      >
        <CheckCircle2 className="h-4 w-4" />
        {processingPhone === key ? 'Снимаем...' : 'Снять отметку'}
      </Button>
    );
  }

  if (group.isProcessed) {
    return (
      <Button size="sm" variant="outline" disabled>
        {`Обработано: ${group.processedByUsername || 'оператор'}`}
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="secondary"
      className="gap-2"
      disabled={processingPhone === key}
      onClick={() => handleMarkProcessed(group)}
    >
      <CheckCircle2 className="h-4 w-4" />
      {processingPhone === key ? 'Метим...' : 'Обработано'}
    </Button>
  );
};

const renderActionButtons = (group, handlers) => {
  if (group.isResolved) {
    return (
      <Button size="sm" disabled className="bg-emerald-600 text-white hover:bg-emerald-600">
        Дозвонился
      </Button>
    );
  }

  return (
    <div className="flex justify-end gap-2">
      {renderClaimButton(group, handlers.claimingPhone, handlers.handleClaim, handlers.handleUnclaim)}
      {renderProcessedButton(group, handlers.processingPhone, handlers.handleMarkProcessed, handlers.handleUnmarkProcessed)}
    </div>
  );
};

const StatCard = ({ label, value, tone }) => {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900'
  };

  return (
    <div className={`rounded-2xl border px-4 py-4 shadow-sm ${tones[tone] ?? tones.slate}`}>
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold">{value}</div>
    </div>
  );
};

const normalizeCallAnalytics = (call) => {
  const rawCallType = typeof call?.callClassificationType === 'string' && call.callClassificationType.trim()
    ? call.callClassificationType.trim()
    : null;
  const negativeCategories = [
    ...(Array.isArray(call?.negativeCategories) ? call.negativeCategories : []),
    ...(Array.isArray(call?.negativeSubCategories) ? call.negativeSubCategories : [])
  ];
  const normalizedNegativeSeverity = typeof call?.negativeSeverity === 'string'
    ? call.negativeSeverity.trim().toLowerCase()
    : '';

  return {
    callType: rawCallType ? (CALL_TYPE_LABELS[rawCallType] ?? rawCallType) : null,
    isNegative: normalizedNegativeSeverity.length > 0 && normalizedNegativeSeverity !== 'no',
    hasComplaint: negativeCategories.some((item) => /жалоб|complaint/i.test(String(item ?? '')))
  };
};

const buildCallAnalyticsMap = (groups) => {
  const analyticsMap = new Map();

  groups.forEach((group) => {
    (group.calls ?? []).forEach((call) => {
      if (!call?.taskId || analyticsMap.has(call.taskId)) {
        return;
      }

      analyticsMap.set(call.taskId, normalizeCallAnalytics(call));
    });
  });

  return analyticsMap;
};

const formatCallCount = (value) => {
  const count = Number(value) || 0;
  if (count <= 1) {
    return '1 звонок';
  }

  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return `${count} звонок`;
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `${count} звонка`;
  }

  return `${count} звонков`;
};

const formatDuration = (totalSeconds) => {
  const duration = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatRelativeWait = (value, now) => {
  const date = normalizeMoscowDate(value);
  if (!date) {
    return '—';
  }

  const diffMs = Math.max(0, now - date.getTime());
  const totalMinutes = Math.round(diffMs / 60000);
  if (totalMinutes < 1) {
    return 'только что';
  }

  if (totalMinutes < 60) {
    return `${totalMinutes} мин назад`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours} ч ${minutes} мин назад` : `${hours} ч назад`;
};

const normalizeMoscowDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const text = typeof value === 'string' ? value.trim() : String(value);
  if (!text) {
    return null;
  }

  const withOffset = /(?:Z|[+-]\d{2}:\d{2})$/i.test(text) ? text : `${text}+03:00`;
  const parsed = new Date(withOffset);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatInMoscow = (value, options) => {
  const date = normalizeMoscowDate(value);
  if (!date) {
    return '—';
  }

  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: MOSCOW_TIME_ZONE,
    ...options
  }).format(date);
};

const formatCallTime = (value) =>
  formatInMoscow(value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const ORDER_STATUS_MAP = {
  unconfirmed: 'Не подтверждён',
  waitcooking: 'Ожидает готовки',
  readyforcooking: 'Готов к готовке',
  cookingstarted: 'Готовится',
  cookingcompleted: 'Приготовлен',
  waiting: 'Ожидает',
  onway: 'В пути',
  delivered: 'Доставлен',
  closed: 'Закрыт',
  cancelled: 'Отменён',
};

const ORDER_TYPE_MAP = {
  delivery: 'Доставка',
  pickup: 'Самовывоз',
  dinein: 'В ресторане',
};

const translateOrderStatus = (status) => {
  if (!status) return '—';
  return ORDER_STATUS_MAP[status.toLowerCase()] ?? status;
};

const translateOrderType = (type) => {
  if (!type) return '—';
  return ORDER_TYPE_MAP[type.toLowerCase()] ?? type;
};

const formatOrderTime = (value) =>
  formatInMoscow(value, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

const formatDateRange = (range) => {
  if (!range?.from || !range?.to) {
    return 'не выбран';
  }

  const from = format(range.from, 'dd.MM.yyyy');
  const to = format(range.to, 'dd.MM.yyyy');
  return from === to ? from : `${from} - ${to}`;
};

const getTodayInMoscow = () => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: MOSCOW_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const parts = formatter.formatToParts(new Date());
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);
  return new Date(year, month - 1, day);
};

const buildGroupClientKey = (group) => `${group.clientPhone}:${group.groupDate ?? ''}`;

const mergeGroupsWithCachedOrders = (groups, cache) =>
  groups.map((group) => {
    const cacheKey = buildGroupClientKey(group);
    const cached = cache.get(cacheKey);
    if (!cached || cached.lastCallTime !== group.lastCallTime) {
      if (cached && cached.lastCallTime !== group.lastCallTime) {
        cache.delete(cacheKey);
      }

      return {
        ...group,
        lastOrder: group.lastOrder ?? null,
        orderLookupCompleted: Boolean(group.orderLookupCompleted)
      };
    }

    return {
      ...group,
      lastOrder: cached.lastOrder,
      orderLookupCompleted: cached.orderLookupCompleted
    };
  });

const findGroupLastCallTime = (groups, item) =>
  groups.find((group) => buildGroupClientKey(group) === buildGroupClientKey(item))?.lastCallTime ?? null;

export default MissedCalls;
